import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, User, TrendingUp } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatINR(amount) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    capital_deployment: ''
  });
  const [commitments, setCommitments] = useState([]);

  useEffect(() => {
    const email = localStorage.getItem('user_email');
    if (!email) {
      navigate('/login');
      return;
    }

    fetchProfile(email);
    fetchCommitments(email);
  }, [navigate]);

  const fetchProfile = async (email) => {
    try {
      const response = await axios.get(`${API}/profile?email=${email}`);
      setProfile(response.data);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchCommitments = async (email) => {
    try {
      const response = await axios.get(`${API}/commitments/my?email=${email}`);
      setCommitments(response.data);
    } catch (error) {
      console.error('Failed to load commitments');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!profile.first_name || !profile.last_name || !profile.phone || !profile.capital_deployment) {
      toast.error('Please fill in all fields');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/profile?email=${profile.email}`, {
        first_name: profile.first_name,
        last_name: profile.last_name,
        phone: profile.phone,
        capital_deployment: parseInt(profile.capital_deployment)
      });
      
      toast.success('Profile updated!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update profile';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <p className="text-[#64748B]">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="!bg-transparent text-[#64748B] hover:text-[#0F172A] hover:!bg-transparent px-0"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Profile Form */}
          <Card className="border-[#E2E8F0] shadow-none">
            <CardHeader>
              <div className="flex items-center gap-2">
                <User className="h-5 w-5 text-brand" />
                <CardTitle className="font-heading text-xl font-bold text-[#0F172A]">MY PROFILE</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSave} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#0F172A] font-medium text-sm">Email</Label>
                  <Input
                    value={profile.email}
                    disabled
                    className="bg-[#F1F5F9] border-[#E2E8F0] h-11"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#0F172A] font-medium text-sm">First Name</Label>
                    <Input
                      value={profile.first_name}
                      onChange={(e) => setProfile({...profile, first_name: e.target.value})}
                      className="bg-white border-[#E2E8F0] h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#0F172A] font-medium text-sm">Last Name</Label>
                    <Input
                      value={profile.last_name}
                      onChange={(e) => setProfile({...profile, last_name: e.target.value})}
                      className="bg-white border-[#E2E8F0] h-11"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#0F172A] font-medium text-sm">Phone Number</Label>
                  <Input
                    value={profile.phone}
                    onChange={(e) => setProfile({...profile, phone: e.target.value})}
                    className="bg-white border-[#E2E8F0] h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#0F172A] font-medium text-sm">Capital Deployment / Year (INR)</Label>
                  <Input
                    type="number"
                    value={profile.capital_deployment}
                    onChange={(e) => setProfile({...profile, capital_deployment: e.target.value})}
                    className="bg-white border-[#E2E8F0] h-11"
                  />
                </div>

                <Button
                  data-testid="save-profile-btn"
                  type="submit"
                  disabled={saving}
                  className="w-full bg-[#10B981] hover:bg-[#059669] text-white h-12 font-semibold"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Commitments */}
          <Card className="border-[#E2E8F0] shadow-none">
            <CardHeader>
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-brand" />
                <CardTitle className="font-heading text-xl font-bold text-[#0F172A]">MY COMMITMENTS</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {commitments.length === 0 ? (
                <p className="text-[#64748B] text-center py-8">No commitments yet</p>
              ) : (
                <div className="space-y-3">
                  {commitments.map((commitment, index) => (
                    <div
                      key={index}
                      className="p-4 bg-[#F8F9FA] rounded-lg border border-[#E2E8F0]"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-semibold text-[#0F172A]">{commitment.deal_name}</h4>
                        <Badge className="bg-brand text-white text-xs">{commitment.deal_type}</Badge>
                      </div>
                      <p className="text-lg font-bold text-[#10B981] mb-1">{formatINR(commitment.amount_inr)}</p>
                      <p className="text-xs text-[#64748B]">
                        {new Date(commitment.committed_at).toLocaleDateString('en-IN', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
